import { type QuizBankFacetsRes } from '@/api/quizBank';

import { type QuizBankFallbackAxis, type QuizBankFilterState } from '../types';

export interface QuizBankSearchFallback {
  axis: QuizBankFallbackAxis;
  patch: Partial<QuizBankFilterState>;
}

const hasAny = (counts: ReadonlyArray<{ count: number }>) =>
  counts.some(entry => entry.count > 0);

/**
 * 키워드 검색이 현재 필터로 0건일 때 풀 필터 하나를 고른다. 순서는 **단원 → 유형 → 학년**.
 *
 * 축별 facet 은 서버가 "그 축의 필터만 뺀" 건수로 세므로(`facetSearchMetaStages`), 0 이 아닌
 * 값이 있는 축을 풀면 다음 조회가 비지 않는 것이 보장된다 — 학년 축 건수는 단원·유형 필터를
 * 유지한 값이라, 단원(학년 종속)이 걸린 채로는 다른 학년이 0 으로 보여 단원을 먼저 푼다.
 * 학년을 옮길 때는 단원도 비운다 — 단원 id 는 학년에 묶여 있어 옮긴 학년에서는 의미가 없다.
 *
 * @return 풀 필터가 없으면 null. 학년이 잡혀 있지 않은 경우(전학년)는 seed 가 학년을 정하므로 학년 축은 건너뛴다.
 */
export const toQuizBankSearchFallback = (
  filter: QuizBankFilterState,
  facets: QuizBankFacetsRes,
): QuizBankSearchFallback | null => {
  if (!filter.query.trim()) return null;

  if (filter.unitIds.length > 0 && hasAny(facets.units)) {
    return { axis: 'units', patch: { unitIds: [] } };
  }
  if (filter.types.length > 0 && hasAny(facets.types)) {
    return { axis: 'types', patch: { types: [] } };
  }
  if (filter.gradeId) {
    const other = facets.grades.find(
      grade => grade.code !== filter.gradeId && grade.count > 0,
    );
    if (other) {
      return { axis: 'grade', patch: { gradeId: other.code, unitIds: [] } };
    }
  }
  return null;
};
